import { apiClient } from './client';

export type SalesResearchStatus =
  | 'queued'
  | 'researching'
  | 'cross_checking'
  | 'synthesizing'
  | 'ready'
  | 'failed'
  | 'cancelled';

export const TERMINAL_STATUSES: SalesResearchStatus[] = ['ready', 'failed', 'cancelled'];

export function isRunActive(status: SalesResearchStatus | undefined): boolean {
  return status !== undefined && !TERMINAL_STATUSES.includes(status);
}

export interface ResearchSourceStatus {
  name: string;
  status: 'ok' | 'failed' | 'not_configured' | 'empty';
  detail: string;
}

export interface ResearchLead {
  name: string;
  title?: string;
  linkedinUrl?: string;
}

export interface SalesResearchRun {
  id: string;
  requestedByUserId: string | null;
  companyName: string;
  companyDomain: string | null;
  leads: ResearchLead[];
  status: SalesResearchStatus;
  progressMessage: string | null;
  sourceStatuses: ResearchSourceStatus[];
  deep: boolean;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  costUsd: string | null;
  createdAt: string;
}

export interface ReportSection {
  id: string;
  heading: string;
  body: string;
}

export interface OutreachAngle {
  trigger: string;
  painHypothesis: string;
  whyItMattersToPersona: string;
  proofPoint: { claim: string; customer: string; source: string } | null;
  messagingAngle: string;
  subjectLineOptions: string[];
  /** Optional: reports written before the messaging guide existed have none. */
  personaMessaging?: {
    persona: string;
    whyThemNow: string;
    opener: string;
    objectionToExpect: string;
  }[];
}

export type ClaimStatus =
  | 'corroborated'
  | 'single_source'
  | 'contested'
  | 'contradicted'
  | 'unverified'
  | 'grounded_internal';

export interface ResearchClaim {
  id: string;
  verbatim: string;
  kind: string;
  status: ClaimStatus;
  method: string;
  providers: string[];
  evidence: { provider: string; url?: string; snippet?: string }[];
  note: string | null;
}

export interface SalesResearchReport {
  id: string;
  runId: string;
  markdown: string;
  sections: ReportSection[];
  outreach: OutreachAngle | null;
  leadBriefs: { name: string; body: string }[];
  citations: { url: string; title?: string }[];
}

export async function startResearchRun(data: {
  companyName: string;
  companyDomain?: string;
  leads?: ResearchLead[];
  deep?: boolean;
}) {
  return apiClient.post<{ runId: string; status: 'queued' | 'already_running' }>(
    '/sales-research/runs',
    data,
  );
}

export async function listResearchRuns() {
  return apiClient.get<{ runs: SalesResearchRun[]; truncated: boolean }>('/sales-research/runs');
}

export async function getResearchRun(runId: string) {
  return apiClient.get<{ run: SalesResearchRun }>(`/sales-research/runs/${runId}`);
}

/** One attempt at sending a brief to one person. Failures are kept too. */
export interface ResearchDelivery {
  id: string;
  recipientEmail: string;
  recipientName: string | null;
  channel: 'email' | 'slack';
  status: 'sent' | 'failed';
  error: string | null;
  sentAt: string;
}

export async function getResearchReport(runId: string) {
  return apiClient.get<{
    run: SalesResearchRun;
    report: SalesResearchReport;
    claims: ResearchClaim[];
    deliveries: ResearchDelivery[];
  }>(`/sales-research/runs/${runId}/report`);
}

export async function cancelResearchRun(runId: string) {
  return apiClient.post<{ success: boolean }>(`/sales-research/runs/${runId}/cancel`, {});
}

export interface DeliveryOutcome {
  email: string;
  status: 'sent' | 'failed';
  messageId?: string;
  error?: string;
}

export async function listRecipients() {
  return apiClient.get<{ recipients: { id: string; name: string; email: string }[] }>(
    '/sales-research/recipients',
  );
}

export async function sendResearchReport(
  runId: string,
  data: { recipients: { email: string; name?: string }[]; note?: string },
) {
  return apiClient.post<{ outcomes: DeliveryOutcome[]; sent: number; failed: number }>(
    `/sales-research/runs/${runId}/send`,
    data,
  );
}

/**
 * The print-ready HTML for a brief.
 *
 * Fetched rather than navigated to in an iframe: `apiClient` refreshes an
 * expired session and retries, and turns a 409 ("report is not ready yet") into
 * a message the caller can show. It returns HTML, not JSON, so it goes through
 * `fetch` directly with the same credentials.
 */
export async function fetchResearchPrintHtml(runId: string): Promise<string> {
  // Warms the session the same way any other call would, so the HTML request
  // below is made with a valid cookie rather than failing on an expired one.
  await apiClient.get(`/sales-research/runs/${runId}`);

  const response = await fetch(`/api/sales-research/runs/${runId}/print`, {
    credentials: 'include',
    // The page is rendered from rows already in hand — it answers in
    // milliseconds or it is not going to. Nothing here should ever leave the
    // caller waiting on a request that has quietly died.
    signal: AbortSignal.timeout(20_000),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      body?.error?.message ?? body?.message ?? `Could not build the PDF (${response.status})`,
    );
  }
  return response.text();
}
